import { Component, OnInit, TemplateRef, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal/index';
import { FormGroup, FormControl } from '@angular/forms';
import { Subject, Subscription, Observable } from 'rxjs';
import { map, debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { ApiService } from 'src/app/shared/services/api.service';
import { ParametrosAutomacao } from 'src/app/shared/models/parametros-automacao';
import { Responsavel, Contato, Cliente } from 'src/app/shared/models/api-clients';
import { OpcoesNotificacaoContatoCliente } from 'src/app/shared/models/opcoes-notificacao-contato-cliente';
import { ExportacaoGenerica } from 'src/app/shared/interfaces/export-config';
import { ExportConfirmComponent } from '../components/export-confirm/export-confirm.component';


export interface ConfigImporterExporterData {
  mode: 'import' | 'export';
  action: 'headoffice' | 'contacts' | 'notifications' | 'automations';
  currentClientId?: number;
  sourceClienteId?: number;
  targetClienteId?: number;
}

export interface ImportData<T> {
  importResult: T;
}

export interface ExportData<T> {
  exportObs: Observable<T>;
}

export interface ConfiguracoesDoSelect {
  singleSelection: boolean;
  text: string;
  selectAllText: string;
  unSelectAllText: string;
  enableSearchFilter: boolean;
  labelKey: string;
  primaryKey: string;
  noDataLabel: string;
  searchPlaceholderText: string;
  lazyLoading: boolean;
  badgeShowLimit: number;
}

@Component({
  selector: 'app-config-importer-exporter',
  templateUrl: './config-importer-exporter.component.html',
  styleUrls: ['./config-importer-exporter.component.scss']
})
export class ConfigImporterExporterComponent implements OnInit, OnDestroy {

  private mode: string;
  private action: string;
  private subscriptions: Subscription[] = [];
  btnText: string;
  clients$: Observable<Cliente[]>;
  keyUp$ = new Subject<KeyboardEvent>();
  private clientExport: ExportacaoGenerica[] = [];
  private dialogExp: MatDialogRef<ExportConfirmComponent>;

  public form: FormGroup = new FormGroup({
    clientsControl: new FormControl(),
    filterMethod: new FormControl('Nome')
  });


  constructor(
    private apiService: ApiService,
    private dialog: MatDialog,
    protected dialogRef: MatDialogRef<ComponentType<any> | TemplateRef<any>>,
    @Inject(MAT_DIALOG_DATA) private data: ConfigImporterExporterData,
  ) {
    if (!this.data || !this.data.mode) {
      throw new Error('"mode" property inside data object is required');
    }

    if (!this.data || !this.data.action) {
      throw new Error('"action" property inside data object is required');
    }

    const configData = (data || {} as ConfigImporterExporterData);
    this.mode = configData.mode;
    this.action = configData.action;
    this.btnText = this.mode === 'import' ? 'Importar' : 'Exportar';

  }

  ngOnInit() {
    this.clients$ = this.form.get('clientsControl').valueChanges
      .pipe(
        startWith(''),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap(val => {
          if (typeof val == 'string') {
            return this.apiService.obterClientes([{
              fieldName: this.form.get('filterMethod').value,
              fieldValue: val
            }, {
              fieldName: 'limite',
              fieldValue: 5
            }]);
          } else {
            return [];
          }
        }),
        map(clis => {
          return clis.filter(cli => cli.id !== this.data.currentClientId);
        })
      );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => {
      if (!s) {
        return;
      }
      s.unsubscribe();
    });
  }






  importHeadOfficeConfig(): Observable<Responsavel[]> {
    const from = this.selectedItems.id;
    return this.apiService.obterResponsaveisCliente(from);
  }

  importContactsConfig(): Observable<Contato[]> {
    const from = this.selectedItems.id;
    return this.apiService.obterContatosCliente(from);
  }

  importNotificationsConfig(): Observable<OpcoesNotificacaoContatoCliente> {
    const from = this.selectedItems.id;
    return this.apiService.obterNotificacoesContatoCliente(from);
  }

  importAutomationsConfig(): Observable<ParametrosAutomacao> {
    const from = this.selectedItems.id;
    return this.apiService.obterParametrosDeNotificacao(from);
  }

  exportHeadOfficeConfig(): Observable<any> {
    const origin = this.data.currentClientId;
    const target = this.selectedItems;
    return this.apiService.exportarResponsaveis(origin, target);
  }

  exportContactsConfig(): Observable<any> {
    const origin = this.data.currentClientId;
    const target = this.selectedItems;
    return this.apiService.exportarContatos(origin, target);
  }

  exportNotificationsConfig(): Observable<any> {
    const origin = this.data.currentClientId;
    const target = this.selectedItems;
    return this.apiService.exportarOpcoesDeNotificacaoContato(origin, target);
  }

  exportAutomationsRiskConfig(): Observable<any> {
    const origin = this.data.currentClientId;
    const target = this.selectedItems;
    return this.apiService.exportarParametrosAutomacaoRiscoAceitavel(origin, target);
  }

  exportAutomationsDeadLineConfig(): Observable<any> {
    const origin = this.data.currentClientId;
    const target = this.selectedItems;
    return this.apiService.exportarParametrosAutomacaoGestaoDePrazos(origin, target);
  }

  exportAutomationsStartDate(): Observable<any> {
    throw new Error('Method not implemented.');
  }

  import(): Observable<any> {
    // action = 'headoffice' | 'contacts' | 'notifications' | 'automations';
    switch (this.action) {
      case 'headoffice': return this.importHeadOfficeConfig();
      case 'contacts': return this.importContactsConfig();
      case 'notifications': return this.importNotificationsConfig();
      case 'automations': return this.importAutomationsConfig();
    }
  }


  export(): Observable<any> {
    // action = 'headoffice' | 'contacts' | 'notifications' | 'automations';
    switch (this.action) {
      case 'headoffice': return this.exportHeadOfficeConfig();
      case 'contacts': return this.exportContactsConfig();
      case 'notifications': return this.exportNotificationsConfig();
      case 'automations-risk': return this.exportAutomationsRiskConfig();
      case 'automations-deadline': return this.exportAutomationsDeadLineConfig();
      case 'automations-startdate': return this.exportAutomationsStartDate();
    }
  }


  // Evento de submit
  exportOrImport(): void {
    if (this.mode === 'import') {
      this.import().subscribe(importData => {
        this.dialogRef.close({
          importResult: importData
        } as ImportData<any>);
      });
    } else {
      const ref = this.export();
      this.dialogRef.close({
        exportObs: ref
      } as ExportData<any>);

      if (ref) {
        this.dialogExp = this.dialog.open(ExportConfirmComponent, {
          panelClass: 'add-export-popup'
        });
      }
    }
  }

  get selectedItems() {
    return this.form.get('clientsControl').value;
  }

  /**
   * Função usada no template para mostrar infos do cliente.
   * @param user
   */
  displayFn(user: Cliente): string {
    let retorno = '';
    if (!user) {
      return retorno;
    }

    retorno = user.nome;

    if (user.cnpjCpf) {
      retorno += ` - CNPJ/CPF ${user.cnpjCpf}`;
    }

    if (user.scp) {
      retorno += ` - SCP: ${user.scp}`;
    }

    if (user.inscricaoEstadual) {
      retorno += ` - IE: ${user.inscricaoEstadual}`;
    }


    return retorno;
  }

  get disableAction() {
    return this.form.get('clientsControl').invalid;
  }

}
